import axios from "axios";

const API = import.meta.env.VITE_API;

import {
	scheduleFromApi,
	stateFromApi,
	periodFromApi,
	exceptionFromApi
} from "../../helpers/scheduler.adapters.js";

export default {
	deviceId: null,
	cache: {},
	currentCache: {},

	setDeviceId(deviceId) {
		this.deviceId = deviceId;
	},

	publishDraftRevision(notes = null, deviceId = null) {
		return axios
			.put(`${API}/devices/${deviceId || this.deviceId}/configs/draft`, {
				publish: true,
				notes: notes
			})
			.then((res) => res.data)
			.catch((err) => {
				throw err.response;
			});
	},

	createDraft(deviceId = null) {
		return axios
			.post(`${API}/devices/${deviceId || this.deviceId}/configs`, {})
			.then((res) => res.data)
			.catch((err) => {
				throw err.response;
			});
	},

	exportDiffAsBlob(deviceId = null) {
		return axios
			.get(
				`${API}/devices/${
					deviceId || this.deviceId
				}/configs/exportDiff`,
				{
					responseType: "blob"
				}
			)
			.then((res) => res.data)
			.catch((err) => {
				throw err.response;
			});
	},

	getDraft(deviceId = null, params, useCache = false) {
		if (useCache && this.cache[deviceId || this.deviceId]) {
			return Promise.resolve(this.cache[deviceId || this.deviceId]);
		}

		return axios
			.get(`${API}/devices/${deviceId || this.deviceId}/configs/draft`, {
				params
			})
			.then((res) => res.data)
			.then((config) => {
				const data = {
					...config,
					scheduler: {
						...config.scheduler,
						schedules: (config.scheduler?.schedules || []).map(
							(schedule) => scheduleFromApi(schedule)
						),
						states: (config.scheduler?.states || []).map((state) =>
							stateFromApi(state)
						),
						applicationPeriods: (
							config.scheduler?.applicationPeriods || []
						).map((period) => periodFromApi(period)),
						exceptions: (config.scheduler?.exceptions || []).map(
							(exception) => exceptionFromApi(exception)
						)
					}
				};
				this.cache[deviceId || this.deviceId] = data;
				return data;
			})
			.catch((err) => {
				throw err.response;
			});
	},

	/**
	 * Fetches the current configuration for a device, optionally returning a cached copy.
	 *
	 * @param {string|null} [deviceId=null] - The device identifier to fetch the config for; falls back to this.deviceId if null.
	 * @param {Object} [params] - Query parameters to include in the request.
	 * @param {boolean} [useCache=false] - If true, return the cached config when available instead of making a network request.
	 * @returns {Promise<Object>} Promise that resolves to the normalized configuration object.
	 * @throws {Object} Throws err.response when the HTTP request fails.
	 */
	getCurrent(deviceId = null, params, useCache = false) {
		if (useCache && this.currentCache[deviceId || this.deviceId]) {
			return Promise.resolve(
				this.currentCache[deviceId || this.deviceId]
			);
		}

		return axios
			.get(
				`${API}/devices/${deviceId || this.deviceId}/configs/current`,
				{
					params
				}
			)
			.then((res) => res.data)
			.then((config) => {
				const data = {
					...config,
					scheduler: {
						...config.scheduler,
						schedules: (config.scheduler?.schedules || []).map(
							(schedule) => scheduleFromApi(schedule)
						),
						states: (config.scheduler?.states || []).map((state) =>
							stateFromApi(state)
						),
						applicationPeriods: (
							config.scheduler?.applicationPeriods || []
						).map((period) => periodFromApi(period)),
						exceptions: (config.scheduler?.exceptions || []).map(
							(exception) => exceptionFromApi(exception)
						)
					}
				};
				this.currentCache[deviceId || this.deviceId] = data;
				return data;
			})
			.catch((err) => {
				throw err.response;
			});
	},

	getDataPointsCount(deviceId = null, revision = "draft") {
		return axios
			.get(
				`${API}/devices/${
					deviceId || this.deviceId
				}/configs/${revision}/dataPoints/count`,
				{
					params: {
						showSpinner: false
					}
				}
			)
			.then((res) => res.data)
			.catch((err) => {
				throw err.response;
			});
	},

	getVirtualPropertiesDataPointsCount(deviceId = null, revision = "draft") {
		return axios
			.get(
				`${API}/devices/${
					deviceId || this.deviceId
				}/configs/${revision}/virtualPropertiesCount`,
				{
					params: {
						showSpinner: false
					}
				}
			)
			.then((res) => res.data)
			.catch((err) => {
				throw err.response;
			});
	},

	deleteDraft(deviceId = null) {
		return axios
			.delete(`${API}/devices/${deviceId || this.deviceId}/configs/draft`)
			.then((res) => res.data)
			.catch((err) => {
				throw err.response;
			});
	},

	getDraftEquipment(deviceId = null, params) {
		const _deviceId = deviceId || this.deviceId;
		return axios
			.get(`${API}/devices/${_deviceId}/configs/draft/equipments`, {
				params
			})
			.then((res) => res.data)
			.catch((err) => {
				throw err.response;
			});
	},

	getDraftProperties(deviceId = null, params) {
		return axios
			.get(
				`${API}/devices/${
					deviceId || this.deviceId
				}/configs/draft/properties`,
				{
					params
				}
			)
			.then((res) => res.data)
			.catch((err) => {
				throw err.response;
			});
	},

	getDraftNetworks(deviceId = null, params) {
		return axios
			.get(
				`${API}/devices/${
					deviceId || this.deviceId
				}/configs/draft/networks`,
				{
					params
				}
			)
			.then((res) => res.data)
			.catch((err) => {
				throw err.response;
			});
	},

	getCurrentProperties(deviceId = null, params) {
		const _deviceId = deviceId || this.deviceId;
		return axios
			.get(`${API}/devices/${_deviceId}/configs/current/properties`, {
				params
			})
			.then((res) => res.data)
			.catch((err) => {
				throw err.response;
			});
	},

	uploadDAconfigFileToCloudHub(formData = null, deviceId = null) {
		const _deviceId = deviceId || this.deviceId;
		return axios
			.post(
				`${API}/devices/${_deviceId}/configs/draft/import?force=true`,
				formData,
				{
					headers: {
						"Content-Type": "multipart/form-data"
					}
				}
			)
			.then((res) => res.data)
			.catch((err) => {
				throw err.response;
			});
	},

	/**
	 * @param {string | null} deviceId - The ID of the device
	 */
	exportHubConfiguration(deviceId = null) {
		const _deviceId = deviceId || this.deviceId;

		return axios
			.get(`${API}/devices/${_deviceId}/configs/current/export`)
			.then((res) => res.data)
			.catch((err) => {
				throw err.response;
			});
	},

	importHubConfiguration(formData = null, deviceId = null) {
		const _deviceId = deviceId || this.deviceId;

		return axios
			.post(
				`${API}/devices/${_deviceId}/configs/draft/import`,
				formData,
				{
					headers: {
						"Content-Type": "multipart/form-data"
					}
				}
			)
			.then((res) => res.data)
			.catch((err) => {
				throw err.response;
			});
	},

	/**
	 *
	 * @param {Object} formula
	 * @param {string | null} deviceId
	 * @returns {Promise}
	 */
	simulate(formula, deviceId = null) {
		return axios.post(
			`${API}/devices/${deviceId || this.deviceId}/configs/draft/virtualProperties/simulate`,
			formula
		);
	}
};
